import { useRef, useState } from "react";
import { Check, FileText, Info, Plus, Trash2, Upload } from "lucide-react";

import type { Dier } from "../../afstand/afstandDieren";

/**
 * Stap 3: de bijlagen, per dier.
 *
 * <p><b>Per dier en niet per aanvraag.</b> Een dierenpaspoort hoort bij één dier. Zet je de
 * bestanden op de aanvraag, dan moet het asiel bij twee dieren zelf uitzoeken welk paspoort bij wie
 * hoort — en dat raadt het aan de bestandsnaam af, wat precies zo vaak misgaat als het klinkt.
 *
 * <p><b>Niet alles is verplicht.</b> Welke stukken een asiel wil zien verschilt per diersoort en per
 * asiel; wat hier verplicht staat komt daarvandaan. Een vast lijstje zou betekenen dat een
 * konijneneigenaar om een stamboom wordt gevraagd.
 *
 * <p><b>De boekjefoto's staan al bij het dier.</b> Ze hier nog eens vragen levert twee kopieën op en
 * de vraag welke de echte is.
 */

/** Wat een asiel kan vragen. Komt later uit de asielinstellingen; dit is de standaardset. */
export interface DocumentSoort {
  sleutel: string;
  naam: string;
  verplicht: boolean;
}

const STANDAARD: DocumentSoort[] = [
  { sleutel: "paspoort", naam: "Dierenpaspoort", verplicht: true },
  { sleutel: "medisch", naam: "Medische rapportage", verplicht: false },
  { sleutel: "overig", naam: "Aanvullend document", verplicht: false },
];

/**
 * Welke bestanden er per dier zijn; de sleutel is dier-id plus documentsoort.
 *
 * <p><b>Een lijst en niet een bestand.</b> Een paspoort is meerdere bladzijden, een medisch verslag
 * loopt over twee kantjes, en een foto van een scherm wordt vaak in twee delen gemaakt. Een plek
 * per soort zou de tweede bladzijde de eerste laten overschrijven -- zonder dat iemand het ziet.
 */
export type Bijlagen = Record<string, { naam: string; bestand: File }[]>;

export function AfstandDocumentenStap({ dieren, bijlagen, zetBijlagen, soorten = STANDAARD }: {
  dieren: Dier[];
  bijlagen: Bijlagen;
  zetBijlagen: (b: Bijlagen) => void;
  soorten?: DocumentSoort[];
}) {
  return (
    <section className="afst__paneel">
      <h3>Documenten per dier</h3>
      <p className="afst__paneeltekst">
        Voeg de gevraagde bestanden toe. Niet alle documenten hoeven voor ieder dier verplicht te
        zijn.
      </p>

      <div className="afst__docrooster">
        {dieren.map((d, i) => (
          <div key={d.id} className="afst__dockolom">
            <p className="afst__dockop">
              {d.naam || `Dier ${i + 1}`}{d.diersoort ? ` · ${d.diersoort}` : ""}
            </p>
            {soorten.map((soort) => {
              const sleutel = `${d.id}:${soort.sleutel}`;
              return (
                <Bijlage
                  key={soort.sleutel}
                  soort={soort}
                  bestanden={bijlagen[sleutel] ?? []}
                  voegToe={(nieuw) => zetBijlagen({
                    ...bijlagen,
                    [sleutel]: [...(bijlagen[sleutel] ?? []),
                      ...nieuw.map((b) => ({ naam: b.name, bestand: b }))],
                  })}
                  haalWeg={(naam) => zetBijlagen({
                    ...bijlagen,
                    [sleutel]: (bijlagen[sleutel] ?? []).filter((b) => b.naam !== naam),
                  })}
                />
              );
            })}
          </div>
        ))}
      </div>

      <p className="afst__tip">
        <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
        De foto's van de entingenboekjes zijn al per dier vastgelegd bij "Uw dieren" en hoeven hier
        niet opnieuw te worden geüpload.
      </p>
    </section>
  );
}

/** Een gevraagd document van een dier; er mogen meerdere bestanden onder hangen. */
function Bijlage({ soort, bestanden, voegToe, haalWeg }: {
  soort: DocumentSoort;
  bestanden: { naam: string; bestand: File }[];
  voegToe: (nieuw: File[]) => void;
  haalWeg: (naam: string) => void;
}) {
  const invoer = useRef<HTMLInputElement>(null);
  const [fout, setFout] = useState<string | null>(null);

  /** Dezelfde grens als de server aanhoudt; hier al weigeren spaart een lange upload voor niets. */
  const MAX = 10 * 1024 * 1024;

  return (
    <div className="afst__bijlage">
      <div className="afst__bijlage-kop">
        <span className="afst__bijlage-icoon" aria-hidden="true"><FileText size={18} /></span>
        <span className="afst__bijlage-tekst">
          <strong>{soort.naam}</strong>
          {bestanden.length === 0
            ? (soort.verplicht ? "Nog niet toegevoegd" : "Optioneel")
            : `${bestanden.length} bestand${bestanden.length === 1 ? "" : "en"}`}
          {fout && <em className="afst__bijlage-fout">{fout}</em>}
        </span>

        {bestanden.length > 0 && (
          <span className="afst__bijlage-klaar"><Check size={15} aria-hidden="true" /> Ontvangen</span>
        )}
        <button type="button" className="afst__knop afst__knop--rustig"
                onClick={() => invoer.current?.click()}>
          {bestanden.length === 0
            ? <><Upload size={16} aria-hidden="true" /> Toevoegen</>
            : <><Plus size={16} aria-hidden="true" /> Nog een</>}
        </button>
      </div>

      {bestanden.length > 0 && (
        <ul className="afst__bijlage-lijst">
          {bestanden.map((b) => (
            <li key={b.naam}>
              <span>{b.naam}</span>
              <button type="button" aria-label={`${b.naam} weghalen`} onClick={() => haalWeg(b.naam)}>
                <Trash2 size={15} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={invoer}
        type="file"
        hidden
        multiple
        accept="image/jpeg,image/png,application/pdf"
        onChange={(e) => {
          const gekozen = Array.from(e.target.files ?? []);
          const teGroot = gekozen.filter((b) => b.size > MAX);
          setFout(teGroot.length === 0 ? null
            : `${teGroot.map((b) => b.name).join(", ")} is groter dan 10 MB en is overgeslagen.`);
          const goed = gekozen.filter((b) => b.size <= MAX);
          if (goed.length > 0) voegToe(goed);
          // Leegmaken, anders levert hetzelfde bestand opnieuw kiezen geen gebeurtenis op.
          e.target.value = "";
        }}
      />
    </div>
  );
}
